<template>
  <transition :name="animation">
    <div v-if="isActive" class="dialog modal is-active" :class="size">
      <div class="full-overlay" @click="cancel('outside')" />
      <div class="modal-card animation-content" :style="{ maxWidth: newWidth }">
        <header v-if="title" class="modal-card-head">
          <p class="modal-card-title">
            {{ title }}
          </p>
        </header>

        <section class="modal-card-body" :class="{ 'is-titleless': !title }">
          <div class="media">
            <div class="media-content">
              <div v-html="message" />

              <div v-if="hasInput" class="field">
                <div class="control">
                  <input ref="input" v-model="prompt" class="input" :class="{ 'is-danger': validationMessage }" @keyup.enter="confirm">
                </div>
                <p class="help is-danger">
                  {{ validationMessage }}
                </p>
              </div>
            </div>
          </div>
        </section>

        <footer class="modal-card-foot">
          <d-btn v-if="showCancel" ref="cancelButton" flat color="gray-700" @click="cancel('button')">
            {{ cancelText }}
          </d-btn>
          <d-btn ref="confirmButton" :color="confirmColor" @click="confirm">
            {{ confirmText }}
          </d-btn>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
  import { defineComponent } from 'vue';
  import { Emitter } from 'mitt';
  import Modal from '../DModal/DModal.vue';
  import { removeElement } from '../../../utils/helpers';

  /**
  * @displayName Dialog
  */
  export default defineComponent({
    name: 'DDialog',
    extends: Modal,
    props: {
      emitter: Emitter,
      title: String,
      message: String,
      confirmColor: {
        type: String,
        default: 'primary-500',
      },
      size: String,
      confirmText: {
        type: String,
        default: 'OK',
      },
      optionalInput: Boolean,
      cancelText: {
        type: String,
        default: 'Cancel',
      },
      hasInput: Boolean, // Used internally to know if it's prompt
      onConfirm: {
        type: Function,
        default: () => {},
      },
      focusOn: {
        type: String,
        default: 'confirm',
      },
      width: {
        type: [String, Number],
        default: 960,
      },
      promptValue: String,
    },

    data() {
      return {
        prompt: '',
        isActive: false,
        validationMessage: '',
        newWidth: typeof this.width === 'number'
          ? `${this.width}px`
          : this.width,
      };
    },

    computed: {
      showCancel() {
        return this.cancelOptions.indexOf('button') >= 0;
      },
    },

    beforeMount() {
      // Insert the Dialog component in body tag
      this.$nextTick(() => {
        document.body.appendChild(this.$el);
      });
    },

    mounted() {
      this.prompt = this.$props.promptValue;
      this.isActive = true;
      this.$nextTick(() => {
        if (this.hasInput) {
          this.$refs.input.focus();
        } else if (this.focusOn === 'cancel' && this.showCancel) {
          this.$refs.cancelButton.$el.focus();
        } else {
          this.$refs.confirmButton.$el.focus();
        }
      });
    },
    methods: {
      /**
       * If it's a prompt Dialog, validate the input.
       * Call the onConfirm prop (function) and close the Dialog.
       */
      confirm() {
        if (this.$refs.input !== undefined && !this.optionalInput) {
          if (!this.$refs.input.checkValidity()) {
            this.validationMessage = this.$refs.input.validationMessage;
            this.$nextTick(() => this.$refs.input.select());
            return;
          }
        }
        this.onConfirm(this.prompt);
        this.close();
      },
      /**
       * Close the Dialog.
       */
      close() {
        this.isActive = false;
        // Timeout for the animation complete before destroying
        setTimeout(() => {
          this.emitter.emit('destroy');
        }, 150);
      },
    },
  });
</script>